import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useErrorHandler } from "../../../common/hooks";
import { TableModel } from "../../Models/TableModel";
import IComponentProps from "../IComponentProps";
import ColumnActions from "./ColumnActions";
import { formatColumnValue } from "./formatColumnValue";
import getComponentLayout from "./getComponentLayout";
import TableCustomFooter from "./TableCustomFooter";

const SORT_ASC = "asc";
const SORT_DESC = "desc";

const TableComponent: React.FC<IComponentProps<TableModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();

  const [rows, setRows] = useState<any[]>([]);
  const [rowsPending, setRowsPending] = useState<boolean>(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: component.PageSizeOptions ? component.PageSizeOptions[0] : 10 });

  const initialSortModel = useMemo(() => {
    const defaultSortColumn = component.Columns.find((col) => col.DefaultSortBy);
    if (defaultSortColumn && defaultSortColumn.Field) {
      return [{ field: defaultSortColumn.Field, sort: (defaultSortColumn.DefaultSortByAscending ?? true) ? SORT_ASC : SORT_DESC }] as GridSortModel;
    }
    return [] as GridSortModel;
  }, [component.Columns]);

  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);

  const handleRefresh = async () => {
    if (!component.GetItems) {
      console.warn("GetItems expression is not defined.");
      return;
    }

    setRowsPending(true);
    try {
      const eventContext = context.CreateChild();
      const sortField = sortModel.length > 0 ? sortModel[0].field : null;
      const sortAscending = sortModel.length > 0 ? sortModel[0].sort === SORT_ASC : true;

      eventContext.VariableScope.set("$event", {
        SortBy: sortField,
        SortAscending: sortAscending,
        PageIndex: paginationModel.page,
        PageSize: paginationModel.pageSize,
      });

      const result = await eventContext.Evaluator.evaluate(component.GetItems, eventContext);
      if (result === undefined || result === null) {
        return;
      }

      // ToDo: allow the user to map the result to the items array
      const items = result.Items as any[];
      const itemsHasMore = result.HasMore ?? false;

      const rowCount = itemsHasMore ? (paginationModel.page + 2) * paginationModel.pageSize : paginationModel.page * paginationModel.pageSize + items.length;
      setRowCount(rowCount);
      if (!Array.isArray(items)) {
        console.error("GetItems expression did not return an array:", result);
        throw new Error("GetItems expression did not return an array: " + result);
      }

      setRows(items);
    } catch (error) {
      errorHandler(error);
    } finally {
      setRowsPending(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () =>
      component.Columns.map(
        (col, index) =>
          ({
            field: col.Field || `__action_column_${index}__`,
            headerName: col.Label,
            width: col.Width,
            flex: col.Flex,
            minWidth: col.MinWidth,
            maxWidth: col.MaxWidth,
            sortable: col.IsSortable ?? false,
            valueFormatter: col.Format ? (value: any) => formatColumnValue(value, col) : undefined,
            renderCell:
              col.ValueTemplate || (col.Actions && col.Actions.length > 0) ? (params: any) => <ColumnActions column={col} row={params.row} /> : undefined,
          }) as GridColDef
      ),
    [component.Columns]
  );

  const getRowId = useCallback(
    (row: any) => {
      const keyField = component.KeyField || "id";
      return row[keyField];
    },
    [component.KeyField]
  );

  // Load data on start
  useEffect(() => {
    handleRefresh();
  }, [component.GetItems, paginationModel, sortModel]);

  const componentLayoutSx = getComponentLayout(component);

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      loading={rowsPending}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      sortingMode="server"
      pageSizeOptions={component.PageSizeOptions ?? [10, 50]}
      rowCount={rowCount}
      paginationMode="server"
      disableRowSelectionOnClick
      slots={{
        footer: () => <TableCustomFooter onRefresh={handleRefresh} isLoading={rowsPending} />,
      }}
      sx={{ ...componentLayoutSx, width: "100%", minHeight: 400, flex: 1, overflow: "auto" }}
    />
  );
};

export default TableComponent;
